import { useState } from "react";
import { api } from "@/api/client";
import { READ_ONLY_TITLE, useReadOnly, useStore } from "@/state/store";
import { useOffsiteConfig } from "@/engine/app/offsite-context";
import type { Team } from "@offsite/shared";

/**
 * Pflicht-Identität nach Code-Login (`OffsiteConfig.identity`), bevor der
 * Editor aufgeht. Anders als das optionale `ar-name`-Feld im CaptainEditor
 * ist das hier ein eigener Schritt: Ohne Name/Abteilung gibt es keine
 * Person, an der der dauerhafte Ergebnis-Link (resultId) hängt.
 */
interface IdentityFormProps {
  n: number;
  onDone: (team: Team) => void;
}

export function IdentityForm({ n, onDone }: IdentityFormProps) {
  const { identity } = useOffsiteConfig();
  const { state } = useStore();
  const token = state.auth.token;
  const readOnly = useReadOnly();
  const [name, setName] = useState("");
  const [abteilung, setAbteilung] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const nameLabel = identity?.nameLabel ?? "Dein Name";
  const abteilungLabel = identity?.abteilungLabel ?? "Deine Abteilung";
  const canSubmit = name.trim().length > 0 && abteilung.trim().length > 0;

  const submit = async () => {
    if (!canSubmit) {
      setErr("Bitte fülle beide Felder aus.");
      return;
    }
    setErr(null);
    setSubmitting(true);
    try {
      const r = await api.teams.setIdentity(
        token,
        n,
        name.trim(),
        abteilung.trim(),
      );
      onDone(r.team);
    } catch {
      // Bewusst laut: sonst tippt jemand seine Anforderung, ohne dass sein
      // Ergebnis je einer Person zugeordnet werden kann.
      setErr("Konnte deine Angaben nicht speichern.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <header style={{ display: "grid", gap: 4 }}>
        <div className="deck-eyebrow">Bevor es losgeht</div>
        <h2
          className="deck-title"
          style={{ fontSize: "clamp(1.4rem, 4.5vw, 1.85rem)", margin: "4px 0 0" }}
        >
          Wer bist du?
        </h2>
        <p className="deck-sub" style={{ marginTop: 8 }}>
          Deine Angaben erscheinen über deinem Ergebnis — und unter deinem
          persönlichen Link findest du es später wieder.
        </p>
      </header>

      <label className="req-field">
        <span className="req-field-label">{nameLabel}</span>
        <input
          className="input-text"
          type="text"
          autoComplete="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={submitting}
          autoFocus
        />
      </label>

      <label className="req-field">
        <span className="req-field-label">{abteilungLabel}</span>
        <input
          className="input-text"
          type="text"
          autoComplete="organization-title"
          value={abteilung}
          onChange={(e) => setAbteilung(e.target.value)}
          disabled={submitting}
        />
      </label>

      {err && <p className="form-error">{err}</p>}

      <button
        type="button"
        onClick={submit}
        disabled={submitting || !canSubmit || readOnly}
        className={`btn btn-primary btn-block btn-lg${readOnly ? " is-readonly" : ""}`}
        title={readOnly ? READ_ONLY_TITLE : undefined}
      >
        {submitting ? "…" : "→ Weiter"}
      </button>
    </div>
  );
}
